import React, { PropsWithChildren, useEffect, useMemo } from 'react'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import MenuList from '@mui/material/MenuList'
import MenuIcon from '@mui/icons-material/Menu'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { Box, Button, MenuLink, Popover, Text } from 'components'
import { BoxProps } from 'components/Box'
import { NavMenuItemWrapper } from 'compositions/NavDrawer'
import { useDrawerSettings, useLogout, useMe, usePopoverState, useViewport } from 'hooks'
import {
  Account as AccountIcon,
  Logout as LogoutIcon,
} from 'icons'
import OrdoLogo from 'theme/ordo-logo.svg'

export type NavBarProps = React.PropsWithChildren<{
  toggleDrawer: (e: React.MouseEvent) => void
  AccountMenuItems?: React.FC<{ me: ReturnType<typeof useMe> }>
  SelectorMenuItems?: React.FC<{ me: ReturnType<typeof useMe>, onSelect: () => void }>
}>


const NavBarAccountMenu: React.FC<Pick<NavBarProps, 'AccountMenuItems'> & Pick<BoxProps, 'sx'>> = ({ AccountMenuItems, sx }) => {
  const me = useMe()
  const { me: user } = me
  const { open, anchorElement, handleClick, handleClose } = usePopoverState()
  const logout = useLogout(false)
  const { isLarge, isExtraLarge } = useViewport()
  const [{ registeredContentDrawers, contentDrawerOpen }] = useDrawerSettings()
  const portalIcons = useMemo(() => registeredContentDrawers?.length > 0, [registeredContentDrawers?.length] )

  if (!user) return null
  if (!isLarge) return null

  return <Box sx={[
    (theme) => ({
      borderRightWidth: (portalIcons && !contentDrawerOpen) ? '1px' : 0,
      borderRightStyle: 'solid',
      borderRightColor: theme.palette.divider,
      pr: (portalIcons && !contentDrawerOpen) ? 2 : 0,
      mr: (portalIcons && !contentDrawerOpen) ? 2 : (portalIcons && contentDrawerOpen && isExtraLarge ? theme.sizes.drawerWidth : 0),
    }),
    ...(Array.isArray(sx) ? sx : [sx]),
  ]}>
    <Button color="inherit" variant="text" onClick={handleClick} fullWidth={false}>
      <AccountIcon sx={{ color: "grey" }} />
      <Text ml={1} mr={1} fontWeight="medium">{`${user.firstName} ${user.lastName}`}</Text>
      { !open && <ExpandMore sx={{ color: 'grey' }} /> }
      { open && <ExpandLess sx={{ color: 'grey' }} /> }
    </Button>

    <Popover
      open={open}
      anchorEl={anchorElement}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{ marginTop: 1 }}
    >
      <MenuList id="account-menu" sx={{ minWidth: '250px' }} onClick={() => handleClose()}>
        { AccountMenuItems && <AccountMenuItems me={me} /> }

        <NavMenuItemWrapper>
          <MenuLink href="/logout" color="inherit" onClick={logout} onTouchEnd={logout}>
            <LogoutIcon sx={{ marginRight: 1 }} />
            Logout
          </MenuLink>
        </NavMenuItemWrapper>
      </MenuList>
    </Popover>
  </Box>
}


export const NavBar: React.FC<PropsWithChildren<NavBarProps>> = ({ children, AccountMenuItems, SelectorMenuItems, toggleDrawer }) => {
  const me = useMe()
  const { token } = me
  const { isMedium, isExtraLarge } = useViewport()
  const [{ navDrawerOpen, contentDrawerOpen }, { clearRegisteredContentDrawers }] = useDrawerSettings()

  useEffect(() => {
    clearRegisteredContentDrawers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer - 1, display: token ? 'block' : 'none' }}>
    <Toolbar sx={{
      display: 'flex',
      alignItems: 'center',
      px: { xs: 1, sm: 2 }
    }}>
      { (!navDrawerOpen || (navDrawerOpen && !isExtraLarge)) && <Box>
        <Button color="inherit" variant="text" onClick={toggleDrawer} fullWidth={false} sx={{
          px: 0,
          minWidth: 0,
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}>
          <MenuIcon fontSize='large' sx={{ mr: { xs: 0, md: 1 } }} />
          { isMedium && <OrdoLogo height="47" /> }
        </Button>
      </Box> }

      <Box minWidth={0} ml={(theme) => navDrawerOpen && isExtraLarge ? theme.sizes.drawerWidth : 0 } flexGrow={1}>
        { isMedium && SelectorMenuItems && <SelectorMenuItems me={me} onSelect={() => ({})} /> }
        { children }
      </Box>

      <Box display="flex" justifySelf="flex-end" justifyContent="flex-end" ml="auto" alignItems="center">
        <NavBarAccountMenu AccountMenuItems={AccountMenuItems} />
        <Box id="NavBarPortal" display={(!contentDrawerOpen || (contentDrawerOpen && !isExtraLarge)) ? 'flex' : 'none'} flexWrap="nowrap">
          {/* Portal for rendering filters, etc. on mobile */}
        </Box>
      </Box>
    </Toolbar>
  </AppBar>
}

export default NavBar
